import React from 'react';
import PropTypes from 'prop-types';
import { getRetinaImage } from '../AmexUtils';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import { resolveBannerConfig } from './bannerResolver';
import { BREAKPOINTS } from '../constants';
import AmexImage from '../AmexImage';
import './AmexBanner.scss';

const AmexBanner = (
  {
    awsFolderName,
    baseRequestUrl,
    bannerConfig = {},
    bannerContext,
    isExtended,
    hasCopyOverlay,
    matchMedia,
  }
) => {
  if (Object.keys(bannerConfig).length === 0) return null;
  const isMobileUX = !matchMedia.lg;
  const resolvedBannerConfig = resolveBannerConfig({
    bannerConfig,
    userCardType: bannerContext?.userCardType,
    isLoggedIn: bannerContext?.isLoggedIn,
  });

  if (Object.keys(resolvedBannerConfig).length === 0) return null;

  const {
    header = '',
    copy = '',
    fine_copy: fineCopy = '',
    variant: styleVariant = '',
    images = {},
    logo_cobrand: logoCobrand,
    image_alt: imageAlt = '',
    other_logo: otherLogo,
    other_logo_alt: otherLogoAlt = '',
    section_id: sectionId,
  } = resolvedBannerConfig;

  const urlPrefix = `${baseRequestUrl}${awsFolderName}/bannerImages/`;
  const urlPrefixLogo = `${baseRequestUrl}${awsFolderName}/logos/`;
  const {
    desktop: desktopImg = {},
    mobile: mobileImg = {},
  } = images;

  const bannerImgName = getRetinaImage(desktopImg, mobileImg, !isMobileUX);
  const bannerImg = `${urlPrefix}${bannerImgName}`;
  const logoResy = getRetinaImage(logoCobrand, logoCobrand, true);

  const sanitizedHeader = sanitizeString(header);
  const sanitizedCopy = sanitizeString(copy);
  const sanitizedFineCopy = sanitizeString(fineCopy);

  const renderBannerContent = () => (
    <>
      <div className="AmexBanner__hero-content">
        <div className="AmexBanner__hero-content--inner">
          {otherLogo && (
            <div className="AmexBanner__hero-content--logo-other">
              <img
                src={`${urlPrefixLogo}${otherLogo}`}
                alt={otherLogoAlt}
              />
            </div>
          )}
          {header && (
            <h1
              id={sectionId || undefined}
              className="AmexBanner__hero-content--title"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedHeader }}
            />
          )}
          {copy && (
            <p
              className="AmexBanner__hero-content--text"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedCopy }}
            />
          )}
          {fineCopy && (
            <p
              className="AmexBanner__hero-content--fine-print"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedFineCopy }}
            />
          )}
          {logoCobrand && (
            <img
              className="AmexBanner__hero-content--logo--cobrand"
              src={`${urlPrefixLogo}${logoCobrand}`}
              alt="Resy Amex logo"
            />
          )}
        </div>
      </div>
    </>
  );

  const renderBannerContentCopyOverlay = () => (
    <>
      <div className="AmexBanner__content">
        {logoResy && (
          <img
            className="AmexBanner__content--logo--resy"
            src={`${urlPrefixLogo}${logoResy}`}
            alt="Resy Amex logo"
          />
        )}
        {header && (
          <h1
            className="AmexBanner__content--title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedHeader,
            }}
          />
        )}
        {copy && (
          <p
            className="AmexBanner__content--text"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedCopy,
            }}
          />
        )}
        {!isMobileUX && fineCopy && (
          <p
            className="AmexBanner__content--textprint"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedFineCopy,
            }}
          />
        )}
      </div>
      {isMobileUX && (
        <div className="AmexBanner__image-wrapper">
          <img
            className="AmexBanner__image-mobile"
            src={bannerImg}
            alt=""
          />

          {fineCopy && (
            <div className="AmexBanner__copy-wrapper">
              <p
                className="AmexBanner__copy-wrapper--textprint"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizedFineCopy,
                }}
              />
            </div>
          )}
        </div>
      )}
    </>
  );

  const renderExtendedContainer = () => (
    <section className={`AmexBanner ${styleVariant}`}>
      {hasCopyOverlay ? (
        <>
          <div className="AmexBanner__image-container">
            <AmexImage
              images={images}
              imageAlt={imageAlt}
              mediaBreakpoint={BREAKPOINTS.MEDIUM}
              matchMedia={matchMedia}
              urlPrefix={urlPrefix}
            />
          </div>
          {renderBannerContent()}
        </>
      ) : (
        <div className="AmexBanner__wrapper">
          <div
            className="AmexBanner__image"
            style={{ backgroundImage: `url(${bannerImg})` }}
          />
          <div className="container">
            <div className="AmexBanner__wrapper--container">
              {renderBannerContentCopyOverlay()}
            </div>
          </div>
        </div>
      )}
    </section>
  );

  const renderNonExtendedContainer = () => (
    <section className="AmexBanner">
      <div className="container">
        {hasCopyOverlay ? (
          <>
            <div className="AmexBanner__image-container">
              <AmexImage
                images={images}
                imageAlt={imageAlt}
                mediaBreakpoint={BREAKPOINTS.MEDIUM}
                matchMedia={matchMedia}
                urlPrefix={urlPrefix}
              />
            </div>
            {renderBannerContent()}
          </>
        ) : (
          <div className="AmexBanner__wrapper">
            <div
              className="AmexBanner__image"
              style={{ backgroundImage: `url(${bannerImg})` }}
            />
            {renderBannerContentCopyOverlay()}
          </div>
        )}
      </div>
    </section>
  );

  return (isExtended ? renderExtendedContainer() : renderNonExtendedContainer());
};

AmexBanner.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  bannerConfig: PropTypes.shape({
    images: PropTypes.shape({
      desktop: PropTypes.shape({}).isRequired,
      mobile: PropTypes.shape({}).isRequired,
    }),
    header: PropTypes.string,
    copy: PropTypes.string,
    variant: PropTypes.string,
    section_id: PropTypes.string,
    other_logo: PropTypes.string,
    other_logo_alt: PropTypes.string,
  }).isRequired,
  bannerContext: PropTypes.shape({
    userCardType: PropTypes.string,
    isLoggedIn: PropTypes.bool,
  }),
  isExtended: PropTypes.bool,
  hasCopyOverlay: PropTypes.bool,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
};

AmexBanner.defaultProps = {
  isExtended: false,
  bannerContext: null,
  hasCopyOverlay: false,
};

export default AmexBanner;
